import { VFC, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormHelperText,
  FormControl,
  Autocomplete,
  Typography,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { BackButton } from "../common/BackButton";
import { AlertDialog } from "../common/dialog/AlertDialog";
import { FormLabel } from "../common/form/FormLabel";
import { FormPullDown, PullDownItem } from "../common/form/FormPullDown";

import {
  TenantEditFormSchema,
  TenantEditForm,
  TenantCreateFormSchema,
  TenantNameRequest,
} from "./api/TenantForm";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { Utility } from "../../utils/Utility";
import Constants from "../../utils/Constants";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { TenantService } from "../tenant/api/TenantService";
import { TenantNameResponse } from "../tenant/api/TenantForm";
import { CancelButton } from "../common/CancelButton";
import TenantEditMode from "../../entity/TenantEditMode";
// import MfaAuthStatus from "../../entity/MfaAuthStatus";
import { CompanyService } from "../company/api/CompanyService";
import { CompanyNameRequest } from "../company/api/CompanyForm";

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { MdmUserNameRequest, MdmUserNameResponse } from "../mdmuser/api/MdmUserForm";
import { MdmUserService } from "../mdmuser/api/MdmUserService";
import MdmUserDialog from "../mdmuser/MdmUserEditDialog";
import MdmUserDialogMode from "../../entity/MdmUserDialogMode";
import CompanyDialogMode from "../../entity/CompanyDialogMode";
import CompanyDialog from "../company/CompanyEditDialog";
import { Loading } from "../common/Loading";
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";

/**
 * テナント編集画面
 */
const TenantEdit: VFC = () => {
  const WIDTH = 10;
  const locate = useLocation();
  const navigate = useNavigate();
  /** locate.state.mode */
  const tenantStatusOptions = Utility.getTenantStatusOption();
  const mfaOptions = Utility.getIsMfaAuthOption();
  const maebaraiOptions = Utility.getIsMaebaraiOption();
  const MODE = locate.state.mode as TenantEditMode;
  const PAGE_NAME = (MODE === TenantEditMode.CREATE) ? "テナント追加" : 
                    (MODE === TenantEditMode.DUPLICATE) ? "テナント複製" : 
                                                            "テナント編集";
  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);

  // テナントID
  const [tenantId] = useState(sessionStorage.getItem("TENANT_ID") ?? "");

  // テナント情報
  const [tenantEditForm, setTenantEditForm] = useState(new TenantEditForm());

  // テナント選択肢
  const [tenantOptions, setTenantOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // 上位操作
  const [upperOperations, setUpperOperations] = useState<PullDownItem[]>(Utility.getUpperOperationOption());

  // 企業選択肢
  const [companyOptions, setCompanyOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // ユーザー選択肢
  const [mdmUserOptions, setMdmUserOptions] = useState<PullDownItem[]>([] as PullDownItem[]);

  // MDMユーザーダイアログ用のID
  const [mdmUserDialogId, setMdmUserDialogId] = useState("");

  // 企業ダイアログ用のID
  const [companyDialogId, setCompanyDialogId] = useState("");

  // 企業ダイアログ用のモード
  const [companyDialogMode, setCompanyDialogMode] = useState(CompanyDialogMode.CREATE);

  // 企業ダイアログ
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  // MDMユーザーダイアログ
  const [isMdmUserOpen, setIsMdmUserOpen] = useState(false);

  // 新規作成・編集・複製確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
    if (resultResponse.status === "OK") {
      navigate(-1);
    }
  };

  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.tenantName) {
      setError("tenantName", { message: error.tenantName });
    }
    if (error.parentId) {
      setError("parentId", { message: error.parentId });
    }
    if (error.upperOperation) {
      setError("upperOperation", { message: error.upperOperation });
    }
    if (error.companyId) {
      setError("companyId", { message: error.companyId });
    }
    if (error.basicInformation) {
      setError("basicInformation", { message: error.basicInformation });
    }
    if (error.administratorId) {
      setError("administratorId", { message: error.administratorId });
    }
    if (error.responsibleId) {
      setError("responsibleId", { message: error.responsibleId });
    }
    if (error.salesRepresentativeName) {
      setError("salesRepresentativeName", { message: error.salesRepresentativeName });
    }
    if (error.tenantStatus) {
      setError("tenantStatus", { message: error.tenantStatus });
    }
    if (error.contractChargePrice) {
      setError("contractChargePrice", { message: error.contractChargePrice });
    }
    if (error.contractStart) {
      setError("contractStart", { message: error.contractStart });
    }
    if (error.contractEnd) {
      setError("contractEnd", { message: error.contractEnd });
    }
    if (error.contractMaxAccount) {
      setError("contractMaxAccount", { message: error.contractMaxAccount });
    }
    if (error.isMfaauth) {
      setError("isMfaauth", { message: error.isMfaauth });
    }
    if (error.isMaebarai) {
      setError("isMaebarai", { message: error.isMaebarai });
    }
    if (error.comment) {
      setError("comment", { message: error.comment });
    }
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    getTenantNameList();
    getCompanyNameList();
    getMdmUserNameList();

    // 上位操作の初期値の入力
    setValue("upperOperation", "0");
    setValue("tenantStatus", "1");
    setValue("contractMaxAccount", "5");
    setValue("isMfaauth", "false");

    if ("" !== tenantId) {
      // テナント情報取得
      findByEditId();
    }
  }, []);

  // テナント名一覧取得API
  const getTenantNameList = () => {
    const service = new TenantService();
    service
      .findTenantNameList(new TenantNameRequest)
      .then((response: AxiosResponse) => {
        const tenantNameList: TenantNameResponse[] = response.data.data;
        setTenantOptions(Utility.getTenantNameOption(tenantNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // 企業名一覧取得API
  const getCompanyNameList = () => {
    const service = new CompanyService();
    service
      .findCompanyNameList(new CompanyNameRequest)
      .then((response: AxiosResponse) => {
        const companyNameList: TenantNameResponse[] = response.data.data;
        setCompanyOptions(Utility.getCompanyNameOption(companyNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // MDMユーザー名一覧取得API
  const getMdmUserNameList = () => {
    const service = new MdmUserService();
    service
      .findMdmUserNameList(new MdmUserNameRequest)
      .then((response: AxiosResponse) => {
        const mdmUserNameList: MdmUserNameResponse[] = response.data.data;
        setMdmUserOptions(Utility.getMdmUserNameOption(mdmUserNameList));
      })
      .catch((err) => {})
      .finally(() => {});
  }

  // テナント情報取得API
  const findByEditId = () => {
    if ("" !== tenantId) {
      setIsLoading(true);
      const service = new TenantService();
      service
        .findByEditId(tenantId)
        .then((response: AxiosResponse) => {
          const tenant = response.data.result;
          setValue("id", tenantId);
          setValue("disclosureCode", tenant.disclosureCode);
          setValue("tenantName", tenant.tenantName);
          setValue("parentId", tenant.parentId);
          setValue("companyId", tenant.companyId);
          setValue("companyName", tenant.companyName);
          setValue("basicInformation", tenant.basicInformation);
          setValue("administratorId", tenant.administratorId);
          setValue("administratorName", tenant.administratorName);
          setValue("responsibleId", tenant.responsibleId);
          setValue("responsibleName", tenant.responsibleName);
          setValue("salesRepresentativeName", tenant.salesRepresentativeName);
          setValue("tenantStatus", tenant.tenantStatus);
          setValue("contractChargePrice", tenant.contractChargePrice);
          setValue("startView", new Date(tenant.contractStart));
          setValue("endView", new Date(tenant.contractEnd));
          setValue("contractMaxAccount", tenant.contractMaxAccount);
          setValue("isMfaauth", tenant.isMfaauth);
          setValue("isMaebarai", tenant.isMaebarai);
          setValue("comment", tenant.comment);
          setValue("createUser", tenant.createUser);
          setValue("createDate", tenant.createDate);
          setValue("updateUser", tenant.updateUser);
          setValue("updateDate", tenant.updateDate);
          setValue("deleteDate", tenant.deleteDate);

          if (!Number.isNaN(+tenant.upperOperation)) {
            setValue("upperOperation", (+tenant.upperOperation).toString());
          }

          setTenantEditForm(response.data.result);

        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
      } else {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          Constants.ERR_CODE_404,
          Constants.T_MSG_NO_DATA,
          Constants.W_MSG_NO_DATA
        );
      }
  };

  /***************************************************************
   * 新規作成・編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 新規作成・編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };

  // 新規作成時にリストにセットする
  const handleSetCompanyId = (id: string) => {
    setValue("companyId", id);
  }
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    watch,
    control,
    formState: { errors },
  } = useForm<TenantEditForm>({
    resolver: yupResolver(
      MODE === TenantEditMode.EDIT ?  TenantEditFormSchema : TenantCreateFormSchema
    ),
    reValidateMode: "onSubmit",
    defaultValues: new TenantEditForm(),
  });

  // テナント情報新規作成確認API or テナント情報編集確認API
  const handleEditBtn: SubmitHandler<TenantEditForm> = (hookForm) => {
    const service = new TenantService();
    switch (MODE) {
      case TenantEditMode.CREATE:
      case TenantEditMode.DUPLICATE:
        // 新規作成 or 複製
        service
          .createConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setTenantEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case TenantEditMode.EDIT:
      // テナント情報編集確認API
        service
          .updateConfirm(hookForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setTenantEditForm(getValues());
              handleEditAlertOpen();
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
    }
  };
  // テナント情報新規作成API or テナント情報編集API
  const handleEditConfirmBtn = () => {
    const service = new TenantService();

    handleEditAlertClose();
    setTenantEditForm(getValues());

    switch (MODE) {
      case TenantEditMode.CREATE:
      case TenantEditMode.DUPLICATE:
        service
          .create(tenantEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
        break;
      case TenantEditMode.EDIT:
        // テナント情報編集API
        service
          .update(tenantEditForm)
          .then((response) => {
            if (Constants.ERR_STATUS_OK === response.data.status) {
              // OK
              setIsDialogOpen(true);
              setResultResponse(response.data);
            } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
              // 入力違反
              setBackendValidMessage(response.data.errorMessages);
            } else {
              // NG
              setIsDialogOpen(true);
              setResultResponse(response.data);
            }
          })
          .catch((err) => {
            setStateResultResponse(
              Constants.ERR_STATUS_NG,
              err.response.status,
              err.response.statusText,
              Constants.E_MSG_SYSTEM_ERR
            );
          })
          .finally(() => {
            // TODO 必要ならローディング
          });
    }
  };

  /***************************************************************
   * 削除処理
   **************************************************************/
  // 削除確認ダイアログ(OPEN)
  const handleDeleteAlertOpen = () => {
    setIsDeleteAlertDialogOpen(true);
  };
  // 削除確認ダイアログ(CLOSE)
  const handleDeleteAlertClose = () => {
    setIsDeleteAlertDialogOpen(false);
  };
  // 削除ボタン押下
  const handleDeleteBtn = () => {
    const service = new TenantService();

    if ("" !== tenantId) {
      setIsDeleteAlertDialogOpen(false);
      service
        .delete(tenantId)
        .then((response) => {
          if ("OK" === response.data.status) {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          } else {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    }
  };

  /***************************************************************
   * MDMユーザー詳細表示処理
   **************************************************************/
  // ユーザーダイヤログ（OPEN）
  const showUserButtonClick = (id: string) => {
    // 画面遷移なしでポップアップ表示
    setMdmUserDialogId(id);
    setIsMdmUserOpen(true);
  }

  // ユーザーダイヤログ（CLOSE）
  const handleMdmUserClose = () => {
    setIsMdmUserOpen(false);
  }
  
  /***************************************************************
   * 企業詳細表示処理
   **************************************************************/
  // 企業ダイヤログ（OPEN）
  const showCompanyButtonClick = (id: string, mode: CompanyDialogMode) => {
    // 画面遷移なしでポップアップ表示
    setCompanyDialogId(id);
    setCompanyDialogMode(mode);
    setIsCompanyOpen(true);
  }

  // 企業ダイヤログ（CLOSE）
  const handleCompanyClose = () => {
    setIsCompanyOpen(false);
  }

  /***************************************************************
   * 権限処理
   **************************************************************/
  // 権限なしのオーバーライド
  const overrideFunction = (response: ResultResponse) => {
    setResultResponse(response);
    setIsDialogOpen(true);
  }

  /***************************************************************
   * レイアウト
   **************************************************************/

  return (
    <>
      <Box>
        {/* ボタンエリア */}
        <ButtonArea pageName={PAGE_NAME}>
          {/* 削除ボタン */}
          <ProtectedApiAuth
            authcd={Constants.AUTH_TENANT_DELETE}
            defaultFunction={handleDeleteAlertOpen}
            overrideFunction={overrideFunction}
          >
            <Button
              variant="contained"
              color="error"
              disabled={MODE !== TenantEditMode.EDIT}
            >
              テナント削除
            </Button>
          </ProtectedApiAuth>
          {/* ボタン1つ分の空白 */}
          <Button
            disabled={true}
          >
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleEditBtn)}
          >
            保存
          </Button>
          <CancelButton/>
        </ButtonArea>

        {/* 入力フォーム */}
        <Box sx={{ p: 4 }}>
          <Box sx={{ py: 2, px: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {/* テナントID */}
              {MODE === TenantEditMode.EDIT &&
                <>
                  <FormLabel label="テナントID" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantId}
                    </Typography>
                  </Grid>
                </>
              }
              {/* 公開コード */}
              {MODE === TenantEditMode.EDIT &&
                <>
                  <FormLabel label="テナントコード" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {getValues("disclosureCode")}
                    </Typography>
                  </Grid>
                </>
              }
              {/* テナント名 */}
              <FormLabel label="テナント名" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("tenantName")}
                    error={"tenantName" in errors}
                    helperText={errors.tenantName?.message}
                  />
                </FormControl>
              </Grid>
              {/* 企業名 */}
              <FormLabel label="企業名" isMandatory={true} />
              <Grid item xs={WIDTH - 1}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="companyId"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("companyId")}
                          error={"companyId" in errors}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {companyOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.companyId && (
                          <FormHelperText error>
                            {errors.companyId?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={1}>
                {watch("companyId") !== "" && companyOptions.map(company => company.key).includes(watch("companyId")) ? <>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_COMPANY_DETAIL}
                    defaultFunction={() => showCompanyButtonClick(getValues("companyId"), CompanyDialogMode.DETAIL)}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <InfoOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                </> : <></>}
                <ProtectedApiAuth
                  authcd={Constants.AUTH_COMPANY_CREATE}
                  defaultFunction={() => showCompanyButtonClick(getValues("companyId"), CompanyDialogMode.CREATE)}
                  overrideFunction={overrideFunction}
                >
                  <IconButton>
                    <AddCircleOutlineOutlinedIcon/>
                  </IconButton>
                </ProtectedApiAuth>
              </Grid>
              {/* 親テナント名 */}
              <FormLabel label="親テナント名" isMandatory={false} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="parentId"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("parentId")}
                          error={"parentId" in errors}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {tenantOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.parentId && (
                          <FormHelperText error>
                            {errors.parentId?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 上位操作 */}
              <FormLabel label="上位からの操作" isMandatory={false} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="upperOperation"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("upperOperation")}
                          error={"upperOperation" in errors}
                        >
                          {upperOperations?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.parentId && (
                          <FormHelperText error>
                            {errors.parentId?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 基本情報 */}
              <FormLabel label="基本情報" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("basicInformation")}
                    error={"basicInformation" in errors}
                    helperText={errors.basicInformation?.message}
                  />
                </FormControl>
              </Grid>
              {/* 管理者名 */}
              <FormLabel label="管理者名" />
              <Grid item xs={watch("administratorId") !== "" && mdmUserOptions.map(user => user.key).includes(watch("administratorId")) ? WIDTH - 1 : WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="administratorId"
                    render={({ field: { ref, onChange, ...field } }) => (
                      <>
                        <Autocomplete
                          {...field}
                          size="small"
                          fullWidth
                          noOptionsText={"候補が見つかりません"}
                          options={mdmUserOptions ?? []}
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, v) =>
                            option.key === v.key
                          }
                          value={mdmUserOptions.filter(item => item.key === watch("administratorId"))[0] ?? null}
                          onChange={(e, value) => {
                            setValue("administratorId", value !== null ? value.key : "");
                          }}
                          renderInput={(params) => {
                            return (
                              <TextField
                                {...field}
                                {...params}
                                fullWidth
                                inputRef={ref}
                              />
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {watch("administratorId") !== "" && mdmUserOptions.map(user => user.key).includes(watch("administratorId")) ? <>
                <Grid item xs={1}>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_MDM_USER_DETAIL}
                    defaultFunction={() => showUserButtonClick(getValues("administratorId"))}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <InfoOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                </Grid>
              </> : <></>}
              {/* 責任者名 */}
              <FormLabel label="責任者名" />
              <Grid item xs={watch("responsibleId") !== ""  && mdmUserOptions.map(user => user.key).includes(watch("responsibleId")) ? WIDTH - 1 : WIDTH}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="responsibleId"
                    render={({ field: { ref, onChange, ...field } }) => (
                      <>
                        <Autocomplete
                          {...field}
                          size="small"
                          fullWidth
                          noOptionsText={"候補が見つかりません"}
                          options={mdmUserOptions ?? []}
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, v) =>
                            option.key === v.key
                          }
                          value={mdmUserOptions.filter(item => item.key === watch("responsibleId"))[0] ?? null}
                          onChange={(e, value) => {
                            setValue("responsibleId", value !== null ? value.key : "");
                          }}
                          renderInput={(params) => {
                            return (
                              <TextField
                                {...field}
                                {...params}
                                fullWidth
                                inputRef={ref}
                              />
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {watch("responsibleId") !== "" && mdmUserOptions.map(user => user.key).includes(watch("responsibleId")) ? <>
                <Grid item xs={1}>
                  <ProtectedApiAuth
                    authcd={Constants.AUTH_MDM_USER_DETAIL}
                    defaultFunction={() => showUserButtonClick(getValues("responsibleId"))}
                    overrideFunction={overrideFunction}
                  >
                    <IconButton>
                      <InfoOutlinedIcon/>
                    </IconButton>
                  </ProtectedApiAuth>
                </Grid>
              </> : <></>}
              {/* 営業担当者名 */}
              <FormLabel label="営業担当者名" isMandatory={true} />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("salesRepresentativeName")}
                    error={"salesRepresentativeName" in errors}
                    helperText={errors.salesRepresentativeName?.message}
                  />
                </FormControl>
              </Grid>
              {/* テナントステータス */}
              <FormLabel label="テナントステータス" isMandatory={true} />
              <Grid item xs={10}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="tenantStatus"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("tenantStatus")}
                          error={"tenantStatus" in errors}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {tenantStatusOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.tenantStatus && (
                          <FormHelperText error>
                            {errors.tenantStatus?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* テナント開設料 */}
              <FormLabel label="テナント開設料" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contractChargePrice")}
                  />
                </FormControl>
              </Grid>
              {/* テナント契約期間 */}
              <FormLabel label="テナント契約期間" isMandatory={true} />
              <Grid item xs={WIDTH} display="flex" alignItems="center">
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                  dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                  localeText={{
                    previousMonth: "前月を表示",
                    nextMonth: "次月を表示",
                  }}
                >
                  <Controller
                    name="startView"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <DatePicker
                            label="契約期間 開始日"
                            slotProps={{
                              textField: {
                                size: 'small',
                                InputLabelProps: { shrink: true },
                                fullWidth: true,
                              },
                            }}
                            {...field}
                          />
                          {errors.startView && (
                            <FormHelperText error>
                              {errors.startView.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
                {/* sx={{ fontWeight: "bold", mx: 4 }} */}
                <Typography component="span">～</Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                  dateFormats={{ monthAndYear: "yyyy年 MM月" }}
                  localeText={{
                    previousMonth: "前月を表示",
                    nextMonth: "次月を表示",
                  }}
                >
                  <Controller
                    name="endView"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <DatePicker
                            label="契約期間 終了日"
                            slotProps={{
                              textField: {
                                size: 'small',
                                InputLabelProps: { shrink: true },
                                fullWidth: true,
                              },
                            }}
                            {...field}
                          />
                          {errors.endView && (
                            <FormHelperText error>
                              {errors.endView.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              {/* 契約最大MDMアカウント数 */}
              <FormLabel label="MDMアカウント登録上限" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("contractMaxAccount")}
                  />
                </FormControl>
              </Grid>
              {/* MFA認証実施可否 */}
              <FormLabel label="MFA認証実施可否" isMandatory={true} />
              <Grid item xs={10}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="isMfaauth"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("isMfaauth")}
                          error={"isMfaauth" in errors}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {mfaOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.isMfaauth && (
                          <FormHelperText error>
                            {errors.isMfaauth?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 契約条件（前払い必須） */}
              <FormLabel label="契約条件（前払い必須）" />
              <Grid item xs={10}>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="isMaebarai"
                    defaultValue=""
                    render={({ field }) => (
                      <>
                        <Select
                          size="small"
                          {...field}
                          {...register("isMaebarai")}
                        >
                          <MenuItem value="">選択してください</MenuItem>
                          {maebaraiOptions?.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.isMaebarai && (
                          <FormHelperText error>
                            {errors.isMaebarai?.message}
                          </FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              {/* 備考 */}
              <FormLabel label="備考" />
              <Grid item xs={WIDTH}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    size="small"
                    {...register("comment")}
                  />
                </FormControl>
              </Grid>
              {MODE === TenantEditMode.EDIT &&
                <>
                  {/* 作成者 */}
                  <FormLabel label="作成者" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantEditForm.createUser}
                    </Typography>
                  </Grid>
                  {/* 作成日時 */}
                  <FormLabel label="作成日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantEditForm.createDate}
                    </Typography>
                  </Grid>
                  {/* 更新者 */}
                  <FormLabel label="更新者" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantEditForm.updateUser}
                    </Typography>
                  </Grid>
                  {/* 更新日時 */}
                  <FormLabel label="更新日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantEditForm.updateDate}
                    </Typography>
                  </Grid>
                  {/* 削除日時 */}
                  <FormLabel label="削除日時" />
                  <Grid item xs={WIDTH}>
                    <Typography noWrap component="div" align="left">
                      {tenantEditForm.deleteDate}
                    </Typography>
                  </Grid>
                </>
              }
              
              {/* 処理ローディング */}
              <Loading isLoading={isLoading} />
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* MDMユーザーダイアログ */}
      <MdmUserDialog
        isAlertDialogOpen={isMdmUserOpen}
        mode="保存"
        mdmUserId={mdmUserDialogId}
        defaultDialogMode={MdmUserDialogMode.DETAIL}
        handleAlertCancel={handleMdmUserClose}
        handleAlertOk={getMdmUserNameList} 
      />

      {/* 企業ダイアログ */}
      <CompanyDialog
        isAlertDialogOpen={isCompanyOpen}
        mode="保存"
        companyId={companyDialogId}
        defaultDialogMode={companyDialogMode}
        handleSetCompanyId={handleSetCompanyId}
        handleAlertCancel={handleCompanyClose}
        handleAlertOk={getCompanyNameList} 
      />

      {/* 新規作成・編集確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="保存しますか？"
        mode="保存"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      />

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message="削除しますか？"
        mode="削除"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default TenantEdit;
